import React from 'react'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import managementService from '../../services/managementService'
import ManagementReportShell from './ManagementReportShell'
import useManagementReport from './useManagementReport'
import { DataTable, DrillLinks, KpiGrid, KpiTile, Section, Unavailable } from './ManagementWidgets'
import {
  applyTextFilters,
  at,
  exportToSpreadsheet,
  flattenMetrics,
  formatINR,
  isMasked,
  isMetric,
  metricNumber
} from './managementUtils'

const LINKS = [
  { label: 'Tax invoice report', to: '/sales/reports/tax-invoice' },
  { label: 'Sales register', to: '/sales/reports/sales-register' },
  { label: 'Proforma invoices', to: '/sales/reports/proforma-invoice' },
  { label: 'Sales quotations', to: '/sales/reports/sales-quotation' }
]

const monthLabel = (key) => {
  const [y, m] = String(key).split('-').map(Number)
  if (!y || !m) return key
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' })
}

export const RevenueTrendChart = ({ trend }) => {
  const rows = (trend || []).map((t) => ({ month: monthLabel(t.month), value: metricNumber(t.invoicedTaxable) }))
  if (!rows.length || rows.every((r) => r.value === null)) return <Unavailable>No invoicing trend for this financial year</Unavailable>
  return (
    <div style={{ width: '100%', height: 240 }}>
      <ResponsiveContainer>
        <BarChart data={rows} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="month" fontSize={11} />
          <YAxis fontSize={11} tickFormatter={(v) => formatINR(v, { compact: true })} width={80} />
          <Tooltip formatter={(v) => formatINR(v)} />
          <Bar dataKey="value" name="Invoiced (pre-tax)" fill="#222448" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

export const PipelineSection = ({ pipeline }) => {
  if (isMasked(pipeline)) return <Unavailable note="Restricted — requires pipeline permission" />
  if (!pipeline || (isMetric(pipeline.status) && !pipeline.byStage)) {
    return <Unavailable note={pipeline?.status?.note || 'No opportunities recorded'} />
  }
  return (
    <>
      <KpiGrid>
        <KpiTile label="Open opportunities" metric={pipeline.openCount} />
        <KpiTile label="Open value" metric={pipeline.openValue} format="currency" />
        <KpiTile label="Weighted value" metric={pipeline.weightedValue} format="currency" />
        <KpiTile label="Won in period" metric={at(pipeline, 'wonInPeriod.count')} hint={pipeline.wonInPeriod?.note} />
        <KpiTile label="Won value" metric={at(pipeline, 'wonInPeriod.value')} format="currency" />
        <KpiTile label="Lost in period" metric={pipeline.lostInPeriod} />
        <KpiTile label="Win rate" metric={pipeline.winRatePercent} format="percent" />
        <KpiTile label="Past close date" metric={pipeline.pastExpectedCloseDate} tone="amber" />
        <KpiTile label="Without owner" metric={pipeline.withoutOwner} tone="amber" />
      </KpiGrid>
      <div style={{ marginTop: 14 }}>
        <DataTable
          rows={pipeline.byStage || []}
          emptyText="No active sales stages configured."
          columns={[
            { key: 'stageName', label: 'Stage' },
            { key: 'defaultProbability', label: 'Probability', render: (v) => (v === null || v === undefined ? '—' : `${v}%`) },
            { key: 'count', label: 'Opportunities', format: 'number' },
            { key: 'value', label: 'Value', format: 'currency' },
            { key: 'weightedValue', label: 'Weighted', format: 'currency' }
          ]}
        />
      </div>
    </>
  )
}

export const TOP_CUSTOMER_COLUMNS = [
  { key: 'customerName', label: 'Customer' },
  { key: 'invoicedTaxable', label: 'Invoiced (pre-tax)', format: 'currency' },
  { key: 'invoiceCount', label: 'Invoices', format: 'number' },
  { key: 'sharePercent', label: 'Share', format: 'percent' }
]

const SalesPipelineRevenueReport = () => {
  const report = useManagementReport(managementService.getSalesPipeline)
  const { filters } = report

  const onExport = (data) => exportToSpreadsheet('sales-pipeline-revenue', {
    Summary: flattenMetrics({ revenue: data.revenue, concentration: data.concentration, salesTarget: data.salesTarget }),
    'Monthly trend': data.monthlyTrend || [],
    'Top customers': data.topCustomers || [],
    'Pipeline by stage': Array.isArray(data.pipeline?.byStage) ? data.pipeline.byStage : []
  })

  return (
    <ManagementReportShell
      title="Sales Pipeline & Revenue"
      subtitle="Invoiced revenue, growth, customer concentration and CRM pipeline"
      report={report}
      onExport={onExport}
    >
      {(d) => (
        <>
          <DrillLinks links={LINKS} />
          <Section title="Revenue">
            <KpiGrid>
              <KpiTile label="Invoiced (pre-tax)" metric={at(d, 'revenue.invoicedTaxable')} format="currency" to="/sales/reports/tax-invoice" />
              <KpiTile label="Invoiced (with tax)" metric={at(d, 'revenue.invoicedWithTax')} format="currency" />
              <KpiTile label="GST (period)" metric={at(d, 'revenue.gstAmount')} format="currency" />
              <KpiTile label="Invoices" metric={at(d, 'revenue.invoiceCount')} />
              <KpiTile label="Previous period" metric={at(d, 'revenue.previousPeriodTaxable')} format="currency" />
              <KpiTile label="Growth" metric={at(d, 'revenue.growthPercent')} format="signedPercent" />
              <KpiTile label="Sales target" metric={d.salesTarget} format="currency" />
              <KpiTile label="Target achievement" metric={d.targetAchievementPercent} format="percent" />
            </KpiGrid>
          </Section>

          <Section title="Invoices & GST — MTD / YTD" subtitle="MTD = calendar month to as-of · YTD = financial year to as-of">
            <KpiGrid>
              <KpiTile label="Invoices MTD" metric={at(d, 'revenue.mtd.invoiceCount')} />
              <KpiTile label="Invoiced MTD (pre-tax)" metric={at(d, 'revenue.mtd.invoicedTaxable')} format="currency" />
              <KpiTile label="Invoiced MTD (with tax)" metric={at(d, 'revenue.mtd.invoicedWithTax')} format="currency" />
              <KpiTile label="GST amount MTD" metric={at(d, 'revenue.mtd.gstAmount')} format="currency" />
              <KpiTile label="Invoices YTD" metric={at(d, 'revenue.ytd.invoiceCount')} />
              <KpiTile label="Invoiced YTD (pre-tax)" metric={at(d, 'revenue.ytd.invoicedTaxable')} format="currency" to="/sales/reports/sales-register" />
              <KpiTile label="Invoiced YTD (with tax)" metric={at(d, 'revenue.ytd.invoicedWithTax')} format="currency" />
              <KpiTile label="GST amount YTD" metric={at(d, 'revenue.ytd.gstAmount')} format="currency" />
            </KpiGrid>
          </Section>

          <Section title="Monthly invoicing trend" subtitle="Financial year to date, pre-tax value">
            <RevenueTrendChart trend={d.monthlyTrend} />
          </Section>

          <Section title="Sales pipeline">
            <PipelineSection pipeline={d.pipeline} />
          </Section>

          <Section title="Customer concentration">
            <KpiGrid>
              <KpiTile label="Billed customers" metric={at(d, 'concentration.customerCount')} />
              <KpiTile label="Top customer share" metric={at(d, 'concentration.topCustomerSharePercent')} format="percent" />
              <KpiTile label="Top 5 share" metric={at(d, 'concentration.top5SharePercent')} format="percent" />
            </KpiGrid>
            <div style={{ marginTop: 14 }}>
              <DataTable
                rows={applyTextFilters(d.topCustomers, filters)}
                columns={TOP_CUSTOMER_COLUMNS}
                emptyText="No customer invoicing in this period."
              />
            </div>
          </Section>
        </>
      )}
    </ManagementReportShell>
  )
}

export default SalesPipelineRevenueReport
